import { use, useEffect, useRef, useState } from "react";
import { LuArrowDownToLine, LuImage, LuMusic, LuVideo } from "react-icons/lu";

import { UserContext } from "@/contexts/user-context";
import { cn } from "@/utils/lib";

import {
  useEditorFieldContext,
  useEditorFileDropContext,
} from "./editor-context";
import { addDroppedFilesToEditorMediaField } from "./editor-upload-utils";

type EditorFileDropAreaProps = {
  className?: string;
  targetLabel: string;
};

const hasDraggedFiles = (dataTransfer?: DataTransfer | null) =>
  Array.from(dataTransfer?.types ?? []).includes("Files");

export const EditorFileDropArea = ({
  className,
  targetLabel,
}: EditorFileDropAreaProps) => {
  const { currentUserPublicKey } = use(UserContext);
  const { enableFileDrop, isFileDragActive, clearFileDragState } =
    useEditorFileDropContext();
  const { setEditorValue, value } = useEditorFieldContext<"media">();
  const dragDepthRef = useRef(0);
  const [isDropTarget, setIsDropTarget] = useState(false);

  useEffect(() => {
    if (!isFileDragActive) {
      dragDepthRef.current = 0;
      setIsDropTarget(false);
    }
  }, [isFileDragActive]);

  if (!enableFileDrop) {
    return null;
  }

  return (
    <div
      className={cn(
        `
          overflow-hidden transition-[max-height,opacity,margin] duration-200
          ease-out
        `,
        isFileDragActive
          ? "mt-[18px] mb-[2px] max-h-52 pb-px opacity-100"
          : "pointer-events-none max-h-0 opacity-0",
        className,
      )}
    >
      <div
        data-editor-file-drop-target="true"
        className={cn(
          `
            relative overflow-hidden rounded-2xl border border-dashed p-4
            shadow-lg backdrop-blur-md
            transition-[border-color,background-color,transform] duration-150
            ease-out
          `,
          isDropTarget
            ? `
              border-primary bg-primary/12 ring-2 shadow-primary/15
              ring-primary/25
            `
            : "border-primary/45 bg-background/80",
        )}
        onDragEnter={(event) => {
          if (!hasDraggedFiles(event.dataTransfer)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          dragDepthRef.current += 1;
          setIsDropTarget(true);
        }}
        onDragLeave={(event) => {
          if (!hasDraggedFiles(event.dataTransfer)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

          if (dragDepthRef.current === 0) {
            setIsDropTarget(false);
          }
        }}
        onDragOver={(event) => {
          if (!hasDraggedFiles(event.dataTransfer)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = "copy";
          setIsDropTarget(true);
        }}
        onDrop={(event) => {
          if (!hasDraggedFiles(event.dataTransfer)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          const files = Array.from(event.dataTransfer.files);

          dragDepthRef.current = 0;
          setIsDropTarget(false);
          clearFileDragState();

          if (!files.length || !currentUserPublicKey) {
            return;
          }

          void addDroppedFilesToEditorMediaField({
            files,
            value,
            setEditorValue,
            currentUserPublicKey,
          });
        }}
      >
        <div
          className="
            pointer-events-none absolute inset-0 bg-linear-to-r from-primary/12
            via-transparent to-primary/6
          "
        />
        <div
          className="
            relative flex flex-col gap-3
            sm:flex-row sm:items-center sm:justify-between
          "
        >
          <div className="flex items-center gap-3">
            <div
              className="
                rounded-2xl bg-primary p-3 text-primary-foreground shadow-sm
              "
            >
              <LuArrowDownToLine className="text-2xl" />
            </div>
            <div className="min-w-0">
              <p
                className="
                  text-[11px] font-semibold tracking-[0.28em] text-primary
                  uppercase
                "
              >
                Drop Zone
              </p>
              <p className="text-sm font-semibold">
                Drop images, videos, or audio here
              </p>
              <p className="text-xs text-muted-foreground">
                Release to attach to {targetLabel}
              </p>
            </div>
          </div>

          <div
            className="
              flex shrink-0 items-center gap-4 text-black
              dark:text-white
            "
          >
            <span title="Images">
              <LuImage className="size-7" />
            </span>
            <span title="Video">
              <LuVideo className="size-7" />
            </span>
            <span title="Audio">
              <LuMusic className="size-7" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
